/**
 * Analyzes lighting layout for potential issues
 */

export function analyzeLayout(sections, jumps, lightType, voltage = '12v') {
  const warnings = [];
  const cautions = [];
  const recommendations = [];

  // Check for long extension runs (data signal loss risk)
  jumps.forEach(jump => {
    const length = jump.length_feet;
    
    if (length > 30) {
      warnings.push({
        type: 'signal_loss',
        severity: 'high',
        message: `Connection from ${jump.from_section} to ${jump.to_section} is ${length}ft - HIGH RISK of data signal degradation`,
        recommendation: 'Consider adding a data repeater/amplifier or reducing distance to under 30ft'
      });
    } else if (length > 20) {
      cautions.push({
        type: 'signal_loss',
        severity: 'medium',
        message: `Connection from ${jump.from_section} to ${jump.to_section} is ${length}ft - potential signal issues`,
        recommendation: 'Monitor for flickering or color inconsistency. Consider data booster if issues occur'
      });
    }
  });

  // Calculate total pixel count and power requirements
  let totalPixels = 0;
  let totalLength = 0;
  
  sections.forEach(section => {
    const pixels = section.pixel_count || 0;
    totalPixels += pixels;
    totalLength += section.length_feet || 0;
  });

  // Power injection recommendations based on pixel count
  // Assuming ~0.06W per LED at full white (most WS2811/WS2812B specs)
  const estimatedWatts = totalPixels * 0.06;
  const estimatedAmps = estimatedWatts / 5; // 5V systems

  if (totalPixels > 300) {
    warnings.push({
      type: 'power',
      severity: 'high',
      message: `Total of ${totalPixels} pixels requires approximately ${estimatedAmps.toFixed(1)}A at 5V (${estimatedWatts.toFixed(0)}W)`,
      recommendation: 'REQUIRED: Use power injection every 150-200 pixels to prevent voltage drop and color shift'
    });
  } else if (totalPixels > 150) {
    cautions.push({
      type: 'power',
      severity: 'medium',
      message: `${totalPixels} pixels will draw approximately ${estimatedAmps.toFixed(1)}A (${estimatedWatts.toFixed(0)}W)`,
      recommendation: 'Recommended: Add power injection at midpoint or every 150 pixels for optimal performance'
    });
  }

  // Check for very long sections
  sections.forEach(section => {
    const length = section.length_feet || 0;
    const pixels = section.pixel_count || 0;
    
    if (length > 50) {
      cautions.push({
        type: 'section_length',
        severity: 'medium',
        message: `Section "${section.name}" is ${length}ft long with ${pixels} pixels`,
        recommendation: 'Consider breaking into smaller sections with power injection points'
      });
    }

    // Check pixel density
    if (length > 0 && pixels > 0) {
      const pixelsPerFoot = pixels / length;
      if (pixelsPerFoot > 20) {
        cautions.push({
          type: 'density',
          severity: 'low',
          message: `Section "${section.name}" has high density (${pixelsPerFoot.toFixed(1)} pixels/ft)`,
          recommendation: 'Ensure adequate power supply and cooling for dense pixel arrangements'
        });
      }
    }
  });

  // Check total wire run length
  const totalWireLength = jumps.reduce((sum, jump) => sum + jump.length_feet, 0);
  if (totalWireLength > 100) {
    recommendations.push({
      type: 'wire_management',
      severity: 'low',
      message: `Total wire run: ${totalWireLength}ft across ${jumps.length} connections`,
      recommendation: 'Plan wire routing carefully and use proper outdoor-rated cable with strain relief'
    });
  }

  // Voltage-based power injection recommendations (only for addressable/pixel lights)
  const isAddressable = ['ws2811_pixels', 'ws2812b_strip', 'addressable', 'rgbic_puck'].includes(lightType);
  
  if (isAddressable && voltage === '5v' && totalPixels > 150) {
    cautions.push({
      type: 'power_injection',
      severity: 'medium',
      message: `5V system with ${totalPixels} pixels requires power injection`,
      recommendation: 'Add power injection every 150 pixels using 18AWG or thicker wire to prevent voltage drop and color shift'
    });
  } else if (isAddressable && voltage === '12v' && totalPixels > 300) {
    recommendations.push({
      type: 'power_injection',
      severity: 'low',
      message: `12V system with ${totalPixels} pixels`,
      recommendation: 'Consider power injection every 300 pixels for optimal performance'
    });
  }

  // Controller capacity warning
  if (totalPixels > 1000) {
    warnings.push({
      type: 'controller',
      severity: 'high',
      message: `${totalPixels} pixels may exceed single controller capacity`,
      recommendation: 'Consider using multiple controllers or a controller with expansion ports'
    });
  }

  return {
    warnings,
    cautions,
    recommendations,
    stats: {
      totalPixels,
      totalLength,
      estimatedWatts: estimatedWatts.toFixed(1),
      estimatedAmps: estimatedAmps.toFixed(2),
      totalWireLength
    }
  };
}
